
import { computed, defineComponent, ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import WebSocketService from "../services/WebSocketService";
import { useDicomStore, DicomFile } from '@/store/dicom'
import { useTimerStore } from '@/store/timer'
import { useUsersStore } from '@/store/users'
import { usePopupStore } from '@/store/popup'
import { useDiskStore } from '@/store/disk';
import { useLoginStore } from '@/store/login';

import lodash from 'lodash';

export default defineComponent({
  setup() {
    const dicomStore = useDicomStore();
    const timerStore = useTimerStore();
    const popupStore = usePopupStore();
    const diskStore = useDiskStore();

    const dicomFilesList = computed(() => dicomStore.getDicomFilesList); // 기존에 업로드 한 dicom file list
    const dicomFile = computed(() => dicomStore.getTargetDicomFile);
    const usersStore = useUsersStore();
    const userId = computed(() => usersStore.getLoginUser?.data.user_id);
    const withCAD = computed(() => usersStore.getWithCAD);
    const isDragOver = ref(false);
    
    const uploadedDicomFilesList = computed(() => dicomStore.getFile);
    const pendingListCount = computed(() => dicomStore.getPendingListCount);
    const fileInput = ref<HTMLInputElement | null>(null); // 파일 입력 요소 참조
    const uploadProgress = computed(() => dicomStore.getUploadProgress);
    const activeDeleteBtn = computed(() => dicomStore.getActiveDeleteBtnState);
    const loading = computed(() => dicomStore.getLoadingState);
    const disableFileSelect = computed(() => dicomStore.getFileSelectState);
    const checkAll = ref(false);
    const selectedCount = ref(0);
    const searchRes = computed(() => dicomStore.getSearchRes);
    const searchText = ref("");
    const sortState = ref<{ order: string; direction: "asc" | "desc" | "default" }>({
      order: "",
      direction: "default",
    });
    const message = ref("");
    const messages = ref<string[]>([]);
    let wsService: WebSocketService | null = null;

    // 메시지 수신 시 처리할 함수
    const handleMessage = (msg: string) => {
      messages.value.push(msg);
    };

    // 메시지 전송
    const sendMessage = () => {
      if (wsService && message.value.trim()) {
        wsService.sendMessage(message.value);
        message.value = "";
      }
    };

    // Drag & Drop 핸들러
    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
      isDragOver.value = true;
    };
    const handleDragEnter = (event: DragEvent) => {
      event.preventDefault();
    };
    const handleDragLeave = (event: DragEvent) => {
      event.preventDefault();
      isDragOver.value = false;
    };

    // 파일 입력창을 클릭하는 함수
    const handleFileClick = () => {
      if (fileInput.value) {
        fileInput.value.click(); // 파일 입력창 클릭
      }
    };

    // 파일 선택 핸들러
    const handleFileInputChange = (event: Event) => {
      const input = event.target as HTMLInputElement;
      if (input.files) {
        const files = Array.from(input.files);
        uploadFiles(files);
      }
    };

    // Drop 이벤트 핸들러
    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      isDragOver.value = false;
      if (event.dataTransfer && event.dataTransfer.files) {
        const files = Array.from(event.dataTransfer.files);
        uploadFiles(files);
      }
    };

    // 파일 업로드 함수
    const uploadFiles = async (files: File[]):Promise<void> => {
      if(dicomStore.setFile(files)) {
        if (fileInput.value) {
          fileInput.value.value = ''; // 선택된 파일 초기화
        }
        await dicomStore.dicomUpload();
      }
    };

    const selectDicomFile = async (dicom: DicomFile): Promise<void> => {
      if (
        !activeDeleteBtn.value &&
        (dicom.status === "Completed" ||
          dicom.status === "Shared Existing" ||
          !withCAD.value) &&
        dicomFile.value !== dicom
      ) {
        dicomStore.setImageLoading(true);
        await dicomStore.getSelectedDicomFile(dicom);
      }
    };

    const handleDeleteClick = () => {
      if(activeDeleteBtn.value) {
        if(selectedCount.value > 0) {
          popupStore.togglePopup('deleteDicomConfirm', true);
        }
      } else {
        if(dicomFilesList.value.length > 0) {
          dicomStore.setActiveDeleteBtnState(true);
          dicomStore.initializeDefaults();
        }
      }
    }

    const handleCancelClick = () => {
      dicomStore.setActiveDeleteBtnState(false);
      dicomStore.setCheckboxState("", false);
      checkAll.value = false;
      selectedCount.value = 0;
    }

    const handleCheckboxClick = (dicom: DicomFile, selected: boolean) => {
      dicomStore.setCheckboxState(dicom.unique_filename, selected);
      isAllSelected();
    }

    const handleCheckboxAllClick = () => {
      dicomStore.setCheckboxState("", !checkAll.value);
      isAllSelected();
    }

    const isAllSelected = () => {
      selectedCount.value = searchRes.value.filter(dicom => dicom.selected).length;
      if(selectedCount.value == searchRes.value.length) {
        checkAll.value = true;
      } else {
        checkAll.value = false;
      }
    }

    const statusText = (dicom: DicomFile) => {
      switch (dicom.status) {
        case "Pending":
          if(withCAD.value) return "대기중";
          else return dicom.score;
        case "In Progress":
          if(withCAD.value) return "분석중";
          else return dicom.score;
        case "Completed":
          return dicom.score;
        case "Shared Existing":
          return dicom.score;
        case "Failed":
          return "실패";
        default:
          return "-";
      }
    };

    const formatStudyDate = (date: string) => {
      if (/^\d{8}$/.test(date)) {
        return `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`;
      }
      return date;
    };

    const searchDicomFile = () => {
      dicomStore.setSearchRes([]);
      const query = searchText.value.trim().toLowerCase(); // 검색어 소문자로 변환

      if (query === "") {
        dicomStore.setSearchRes(dicomFilesList.value);
      } else {
        dicomStore.setSearchRes(dicomFilesList.value.filter(file => {
          return (
            file.patient_id?.toLowerCase().includes(query) ||
            file.patient_name?.toLowerCase().includes(query) ||
            file.study_date?.toLowerCase().includes(query)
          );
        }));
      }

      if(sortState.value.order != "") {
        sortData();
      }
    };

    const toggleListSort = (order: string) => {
      if (sortState.value.order === order) {
        if (sortState.value.direction === "desc") {
          sortState.value.direction = "asc";
        } else if (sortState.value.direction === "asc") {
          sortState.value.direction = "default";
          sortState.value.order = ""; // 정렬 기준도 없앰
        } else {
          sortState.value.direction = "desc";
        }
        sortData();
      } else {
        sortState.value.order = order;
        sortState.value.direction = "desc";
        sortData();
      }
    };

    const sortData = () => {
      const { order, direction } = sortState.value;

      // "default"일 때: unique_id 기준 오름차순 정렬
      if (direction === "default") {
        searchRes.value.sort((a, b) => {
          const idA = Number(a.unique_id);
          const idB = Number(b.unique_id);
          return idA - idB;
        });
        return;
      }

      // asc 또는 desc일 때
      searchRes.value.sort((a, b) => {
        let result = 0;

        switch (order) {
          case "id":
            result = a.patient_id?.toString().localeCompare(b.patient_id?.toString());
            break;
          case "name":
            result = a.patient_name?.toString().localeCompare(b.patient_name?.toString());
            break;
          case "score": {
            const numA = isNaN(Number(a.score)) ? a.score : Number(a.score);
            const numB = isNaN(Number(b.score)) ? b.score : Number(b.score);
            result = numA < numB ? -1 : numA > numB ? 1 : 0;
            break;
          }
          case "confirm":
            result = Number(a.is_reviewed) - Number(b.is_reviewed);
            break;
          case "date": {
            const parseDate = (str: string): number | null => {
              if (str === "Anonymized") return null;
              const formatted = `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
              const date = new Date(formatted);
              return isNaN(date.getTime()) ? null : date.getTime();
            };

            const dateA = parseDate(a.study_date);
            const dateB = parseDate(b.study_date);

            if (dateA === null && dateB === null) result = 0;
            else if (dateA === null) result = 1;
            else if (dateB === null) result = -1;
            else result = dateA - dateB;
            break;
          }
        }

        return direction === "asc" ? result : -result;
      });
    };

    const isTextTruncated = (el: HTMLElement) => {
      return el.scrollWidth > el.clientWidth;
    }

    const handleMouseEnterId = (e: MouseEvent, dicom: DicomFile) => {
      const target = e.currentTarget as HTMLElement;
      dicom.showIdTooltip = isTextTruncated(target);
    }

    const handleMouseLeaveId = (dicom: DicomFile) => {
      dicom.showIdTooltip = false;
    }

    const handleMouseEnterName = (e: MouseEvent, dicom: DicomFile) => {
      const target = e.currentTarget as HTMLElement;
      dicom.showNameTooltip = isTextTruncated(target);
    }

    const handleMouseLeaveName = (dicom: DicomFile) => {
      dicom.showNameTooltip = false;
    }

    const handleMouseEnterDate = (e: MouseEvent, dicom: DicomFile) => {
      const target = e.currentTarget as HTMLElement;
      dicom.showDateTooltip = isTextTruncated(target);
    }

    const handleMouseLeaveDate = (dicom: DicomFile) => {
      dicom.showDateTooltip = false;
    }

    watch(dicomFilesList, (newVal) => {
      if(newVal.length == 0) {
        dicomStore.setActiveDeleteBtnState(false);
      }
      selectedCount.value = 0;
      checkAll.value = false;
      searchDicomFile();
    });

    // WebSocket 연결
    onMounted(() => {
      const loginStore = useLoginStore();
      watch(
        () => userId.value,
        async (newUserId) => {
          if (newUserId) {
            const token = await loginStore.checkLastLoginToken();
            if (!token || !newUserId) return;

            if (wsService) {
              wsService.disconnect();
            }
            wsService = new WebSocketService(newUserId, loginStore.getAccessToken);
            wsService.connect(handleMessage);

            await dicomStore.getDicomList();
            searchDicomFile();
          }
        },
        { immediate: true } // 마운트 시에도 한 번 실행
      );

      const handleKeydown = (event: KeyboardEvent) => {
        if (
          pendingListCount.value > 0 &&
          (event.key === "F5" || 
            (event.ctrlKey && event.key.toLowerCase() === "r") || 
            (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "r"))
        ) {
          event.preventDefault();
          const confirmReload = confirm("업로드 대기중인 파일은 저장되지 않을 수 있습니다.");
          if (confirmReload) {
            location.reload();
          }
        }
      };
      window.addEventListener("keydown", handleKeydown);

      onUnmounted(() => {
        wsService?.disconnect(); // WebSocket 해제
        window.removeEventListener("keydown", handleKeydown); // 이벤트 리스너 제거
        dicomStore.setActiveDeleteBtnState(false);
        dicomStore.initDicomList();
      });
    });

    return {
      isDragOver,
      uploadProgress,
      handleFileInputChange,
      handleDragOver,
      handleDragEnter,
      handleDragLeave,
      handleDrop,
      handleFileClick,
      fileInput,
      uploadedDicomFilesList,
      userId,
      dicomFilesList,
      dicomFile,
      selectDicomFile,
      statusText,
      pendingListCount,
      activeDeleteBtn,
      handleDeleteClick,
      handleCancelClick,
      handleCheckboxClick,
      handleCheckboxAllClick,
      checkAll,
      selectedCount,
      searchRes,
      searchText,
      searchDicomFile,
      withCAD,
      formatStudyDate,
      loading,
      disableFileSelect,
      toggleListSort,
      sortData,
      sortState,
      isTextTruncated,
      handleMouseEnterId,
      handleMouseLeaveId,
      handleMouseEnterName,
      handleMouseLeaveName,
      handleMouseEnterDate,
      handleMouseLeaveDate,
      // resize,
    };
  },
});
